import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import { setActiveTunnelIndex } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { validateTunnel } from '../validation/validationController';
import { compareTunnelAddress, formatTunnelAddress, getVisibleTunnelIndexes } from '../addressing';
import { airflowFlipPatch } from './hybridXOffset';
import { refetchHybridLugPlacements } from '../boardActions';

const MAX_VISIBLE_TUNNELS = 5;

/** `tunnel_airflow_directions` holds exactly two CMS rows — 1 left-to-right,
 * 2 right-to-left — and `airflowFlipPatch` swaps a tunnel between them.
 * Tested for 2, never for 1, so that the two arrows stay exact complements
 * (one visible, never both) however the id arrives — the pair used to test
 * for 2 and for 1 and drew BOTH arrows for anything else. `airflowFlipPatch`
 * reads the same way in its `x_offset` branch but picks the next direction
 * with `=== 1`, so for a tunnel with no stored direction the arrow shown and
 * the flip sent can disagree; `AHUTunnels.save()` refills a falsy one, which
 * is what keeps that unreachable. */
const AIRFLOW_RTL = 2;

interface TunnelIndexesProps {
  tunnels: IAhuBoardTunnel[];
  /** Hybrid AHU (KA-x) — pills show the full row:level:position address
   * (each pill isolates one physical tunnel, so back-to-back peers need
   * their own distinct label, unlike the grouped Data Bar tunnel list). */
  isHybrid?: boolean;
  /** Used only to find which tunnel owns the currently selected section, for
   * the persistent `.selected-tunnel` mark (independent of the isolation
   * filter below). */
  sections: IAhuBoardSection[];
}

/**
 * Phase 6b — #tunnels-indexes pills (tmpl/canvas/tunnel.indexes.tmpl §2.3,
 * re-homed from the canvas's jQuery render): `all` pill + one per tunnel
 * showing tunnel.index ("1:1"), `.active` from the slice (active_id —
 * 'all' when none isolated). Rendered only when ≥2 tunnels AND the canvas
 * has asked for them (renderTunnelsIndexes → tunnelIndexesVisible).
 *
 * Clicks are the MainView contract (§2.4 changeIndex): `.active` swap +
 * `canvas:change:tunnel:index (id, prev_id)` on the bus — the 6b canvas
 * consumes it (changeIndex → hideOrShowTunnels over the 6c registries).
 *
 * KA-x-user-skill additions ported here (renderTunnelsIndexes/
 * renderTunnelIndexesHtml/markSelectedTunnel, canvas.view3d.js):
 * - row:level:position sort (matches the Data Bar order, not insertion
 *   order), and 3-field addressing only for hybrid AHUs.
 * - Pagination: at most MAX_VISIBLE_TUNNELS pills shown at once, windowed
 *   and re-centered on whichever of the isolation filter / the currently
 *   selected section's tunnel last changed. Pure arrow clicks shift the
 *   window WITHOUT re-centering (mirrors legacy's shiftTunnelIndexesWindow
 *   vs ensureTunnelIndexWindowVisible split).
 * - `.selected-tunnel`: marks the tab for the tunnel that owns the
 *   currently selected section, independent of the isolation filter (which
 *   stays on "all" unless the user explicitly isolates a tunnel).
 *
 * - the inline Reverse Direction toggle (the pair of
 *   `.btn-tunnel-airflow-direction` labels under the label, one per
 *   direction with the inactive one `.hide`-den — canvas.view3d.js
 *   `airflowDirection`). It duplicates the mutation ConfigureTunnel.tsx and
 *   AhuCanvas.tsx already own, which is why 6b left it out; it is a
 *   user-visible affordance in KA-x-user-skill all the same, so it is back.
 *   On a HYBRID AHU every visible pill carries its own toggle, drawn from
 *   that tunnel's own `airflow_direction_id` and flipping that tunnel — a hybrid unit mixes directions tunnel by
 *   tunnel (counter-flow decks run right-to-left against their neighbours),
 *   so one arrow for whichever tunnel happens to own the selected section
 *   says nothing about the other ten. A standard AHU keeps the single
 *   toggle on the selected section's tunnel exactly as before.
 *
 * NOT ported: the commented-out `.btn-tunnel-x-anchor` shift-left/right
 * buttons in tunnels.section.tmpl — dead markup in KA-x-user-skill itself
 * (wrapped in a Handlebars comment), never actually shipped there either.
 */
const TunnelIndexes = ({ tunnels, isHybrid, sections }: TunnelIndexesProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const airflowInFlight = useRef(false);
  /** The one tunnel whose flip is in flight — `.non-clickable` goes on that
   * arrow alone, rather than dimming all five for one click. (The hook's
   * `isLoading` + `originalArgs` could name the same tunnel; this is state
   * the component already has to release in the same `finally` as the
   * in-flight ref, and keeping the two together is what makes "disabled"
   * and "locked" start and end on the same line.) */
  const [airflowPendingId, setAirflowPendingId] = useState<number | null>(null);
  const visible = useAppSelector((s) => s.ahuBoard.canvas.tunnelIndexesVisible);
  const activeId = useAppSelector((s) => s.ahuBoard.canvas.activeTunnelIndexId);
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const [visibleStart, setVisibleStart] = useState(0);

  const sorted = useMemo(() => [...tunnels].sort(compareTunnelAddress), [tunnels]);

  const selectedTunnelId = useMemo(() => {
    if (currentSectionId == null) return null;
    return sections.find((s) => s.id === currentSectionId)?.tunnel_id ?? null;
  }, [sections, currentSectionId]);

  // ensureTunnelIndexWindowVisible (canvas.view3d.js) — re-centers on
  // whichever of the isolation filter or the selected section's tunnel is
  // live right now (isolation wins when both apply, same as legacy since
  // markSelectedTunnel no-ops while the isolation filter already targets a
  // tunnel — real_index still resolves off activeTunnelIndex there).
  useEffect(() => {
    const targetId = activeId !== 'all' ? activeId : selectedTunnelId;
    const targetIndex = targetId != null ? sorted.findIndex((t) => t.id === targetId) : -1;
    setVisibleStart(getVisibleTunnelIndexes(targetIndex >= 0 ? targetIndex : 0, sorted.length, MAX_VISIBLE_TUNNELS).start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, selectedTunnelId, sorted.length]);

  if (!visible || tunnels.length < 2) return <div id="tunnels-indexes" />;

  const total = sorted.length;
  const showNav = total > MAX_VISIBLE_TUNNELS;
  const maxStart = Math.max(0, total - MAX_VISIBLE_TUNNELS);
  const start = Math.max(0, Math.min(visibleStart, maxStart));
  const end = Math.min(total, start + MAX_VISIBLE_TUNNELS);
  const centerSlot = Math.floor(MAX_VISIBLE_TUNNELS / 2);
  const activeIndex = activeId !== 'all' ? sorted.findIndex((t) => t.id === activeId) : -1;
  const visibleTunnels = sorted.slice(start, end);

  // MainView.changeIndex (main.js:116): fires even on the already-active pill
  // (re-click of 'all'/same tunnel re-runs hideOrShowTunnels) — kept verbatim.
  const onPillClick = (id: number | 'all') => {
    const prev = activeId;
    dispatch(setActiveTunnelIndex(id));
    ahuBoardBus.emit('canvas:change:tunnel:index', id, prev);
  };

  /**
   * A click on the arrow that is mid-PATCH falls THROUGH to the pill —
   * `pointer-events: none` takes the label out of hit-testing, it does not
   * absorb the click — and the pill's own handler would isolate that tunnel
   * in 3D. So the second click of a double-click on the arrow would quietly
   * change what the canvas shows, which is the opposite of what disabling it
   * is for. Swallow a click that lands inside the pending arrow's own box;
   * a click anywhere else on the strip is untouched.
   *
   * `:not(.hide)` is load-bearing. A pill renders BOTH direction labels and
   * hides the one that isn't current, and the pending class goes on both, so
   * without it a right-to-left tunnel — every counter-flow deck — matched the
   * hidden left-to-right label first, measured its all-zero rect, and let the
   * click through to the pill after all.
   */
  const landedOnPendingArrow = (e: React.MouseEvent<HTMLDivElement>) => {
    const pending = e.currentTarget.querySelector(
      '.btn-tunnel-airflow-direction.non-clickable:not(.hide)'
    );
    if (!pending) return false;
    const r = pending.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  };

  // shiftTunnelIndexesWindow (canvas.view3d.js) — shifts the visible window
  // WITHOUT touching which tunnel is active/selected.
  const shiftWindow = (direction: number) => {
    setVisibleStart(Math.max(0, Math.min(maxStart, start + direction)));
  };

  // canvas.view3d.js `airflowDirection` — same PATCH as ConfigureTunnel's
  // toggle (model/tunnel.js `toggle_airflow_direction`, one in flight at a
  // time; the hybrid x_offset re-base lives in the shared airflowFlipPatch,
  // exactly as legacy kept it in the one model method all three affordances
  // called). stopPropagation matters: the icon sits inside the pill, and
  // without it flipping the direction would also isolate that tunnel in 3D.
  const toggleAirflow = (e: React.MouseEvent, tunnel: IAhuBoardTunnel) => {
    e.stopPropagation();
    if (airflowInFlight.current) return;
    airflowInFlight.current = true;
    // Two guards, and they are deliberately not the same size. The ref is
    // strip-wide and is the real lock — one flip at a time, legacy's rule,
    // and it also catches the second click of a double-click, which fires
    // before React can repaint. `.non-clickable` is per arrow and is what
    // the user sees: the arrow they clicked goes out of hit-testing, the
    // others stay live. So a click on another tunnel's arrow during a save
    // is dropped by the ref without looking dropped — the one wrinkle in
    // the arrangement, and a deliberate trade against dimming all five.
    setAirflowPendingId(tunnel.id);
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: airflowFlipPatch(tunnel, !!isHybrid) })
      .unwrap()
      .then(() => {
        // Hybrid: the flip restates the tunnel's lug placement server-side —
        // `valid_kinds` mirrors upstream/downstream and the base groups can be
        // rebuilt off the renumbered addressing. Re-read it for the whole unit
        // (refetchHybridLugPlacements).
        void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: false,
        });
        void dispatch(validateTunnel(tunnel.id));
      })
      .catch(() => undefined)
      // Released on both outcomes: a failed PATCH leaves the arrow as it
      // was, and a dead control is a worse answer than letting the user try
      // again (legacy left its own `.non-clickable` stuck on error).
      .finally(() => {
        airflowInFlight.current = false;
        setAirflowPendingId(null);
      });
  };

  return (
    // `hybrid-strip` scopes the hybrid-only looks (full-opacity arrows) to a
    // hybrid AHU without touching the standard strip, and carries the extra
    // specificity those rules need to beat the legacy sheet, which is
    // injected after this bundle and ties them otherwise.
    <div id="tunnels-indexes" className={isHybrid ? 'hybrid-strip' : undefined}>
      <div
        data-id="all"
        className={`tunnel-index${activeId === 'all' ? ' active' : ''}`}
        onClick={() => onPillClick('all')}
      >
        all
      </div>
      <div className="tunnel-indexes-list">
        {visibleTunnels.map((t, i) => {
          const realIndex = start + i;
          const isActive = realIndex === activeIndex;
          const isCentered = total > MAX_VISIBLE_TUNNELS && isActive && realIndex - start === centerSlot;
          const isSelected = selectedTunnelId != null && t.id === selectedTunnelId;
          // Hybrid: every pill gets its own toggle. Standard: only the
          // selected section's tunnel, unchanged.
          const showAirflow = isHybrid || isSelected;
          return (
            <div
              key={t.id}
              data-index={realIndex}
              data-id={t.id}
              className={`tunnel-index${isActive ? ' active' : ''}${isCentered ? ' tunnel-index-centered' : ''}${
                isSelected ? ' selected-tunnel' : ''
              }${showAirflow ? ' has-airflow-direction' : ''}`}
              onClick={(e) => {
                if (landedOnPendingArrow(e)) return;
                onPillClick(t.id);
              }}
            >
              {/* The address is wrapped so the hybrid strip can carry the
                * pill's dimming on the text alone: `opacity` on the pill
                * multiplies into the arrow below it, and nothing a child
                * declares can undo that, so the arrow can only reach
                * opacity 1 if the pill itself stays at 1. */}
              <span className="tunnel-index-label">
                {isHybrid ? formatTunnelAddress(t, true) : t.index}
              </span>
              {showAirflow && (
                <>
                  <label
                    data-tunnel-id={t.id}
                    className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip left-to-right left-to-right-${t.id}${
                      Number(t.airflow_direction_id) === AIRFLOW_RTL ? ' hide' : ''
                    }${airflowPendingId === t.id ? ' non-clickable' : ''}`}
                    aria-disabled={airflowPendingId === t.id}
                    {...{ tooltip: 'Reverse Direction' }}
                    onClick={(e) => toggleAirflow(e, t)}
                  />
                  <label
                    data-tunnel-id={t.id}
                    className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip right-to-left right-to-left-${t.id}${
                      Number(t.airflow_direction_id) !== AIRFLOW_RTL ? ' hide' : ''
                    }${airflowPendingId === t.id ? ' non-clickable' : ''}`}
                    aria-disabled={airflowPendingId === t.id}
                    {...{ tooltip: 'Reverse Direction' }}
                    onClick={(e) => toggleAirflow(e, t)}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      {showNav && (
        <div className="tunnel-indexes-nav">
          <label
            className={`icon-chevron-left tunnel-indexes-arrow tunnel-indexes-arrow-left${start <= 0 ? ' disabled' : ''}`}
            onClick={() => start > 0 && shiftWindow(-1)}
          />
          <label
            className={`icon-chevron-right tunnel-indexes-arrow tunnel-indexes-arrow-right${end >= total ? ' disabled' : ''}`}
            onClick={() => end < total && shiftWindow(1)}
          />
        </div>
      )}
    </div>
  );
};

export default TunnelIndexes;
